import { ref, reactive, computed, watch, toValue } from 'vue'
import { useWatchlistStore } from '@/stores'
import { CHANGE_SORTS, CHANGE_GROUPINGS } from '@/helpers/watchlistChanges'

const STORAGE_KEY = 'saniteta:watchlist-changes-view'
const DEFAULT_PREFERENCES = { sort: 'unread_first', groupBy: 'none' }

// Ordering and grouping are a personal habit worth remembering; filters are not (they'd hide
// changes the next time the tab opens), so those start empty on every visit.
const loadPreferences = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY))
    return {
      sort: CHANGE_SORTS.includes(stored?.sort) ? stored.sort : DEFAULT_PREFERENCES.sort,
      groupBy: CHANGE_GROUPINGS.includes(stored?.groupBy) ? stored.groupBy : DEFAULT_PREFERENCES.groupBy
    }
  } catch {
    return { ...DEFAULT_PREFERENCES }
  }
}
const savePreferences = ({ sort, groupBy }) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ sort, groupBy }))
  } catch {
    // Storage can be unavailable (private mode, blocked site data); the view still works.
  }
}

/**
 * Everything the Changes tab and the change detail page share for one watchlist: the view
 * (sort / grouping / filters), the loaded changes in that order, loading older ones, and seen
 * state. It lives on the parent page so opening a change and stepping to the next one follows
 * the exact order the list was showing.
 *
 * The list is frozen against its OWN actions while it is on screen: marking a change seen
 * patches it in place instead of refetching, so rows never jump under the pointer from
 * something you did. A realtime arrival is different — the whole point of the websocket push
 * is that you see it now, so `hasNewChanges` auto-triggers `refresh()` instead of waiting for
 * a manual click.
 */
export const useWatchlistChanges = ({ watchlistId, watchlist, enabled = true }) => {
  const { indexChanges, markSeen, markAllSeen } = useWatchlistStore()

  const view = reactive({ ...loadPreferences(), reason: [], entityType: [], unseen: false })
  const setView = patch => {
    Object.assign(view, patch)
    savePreferences(view)
  }
  const resetFilters = () => setView({ reason: [], entityType: [], unseen: false })

  const query = indexChanges({
    watchlistId,
    view: computed(() => ({ sort: view.sort, reason: [...view.reason], entityType: [...view.entityType], unseen: view.unseen })),
    enabled
  })
  const { mutateAsync: markChangeSeen } = markSeen()
  const { mutateAsync: markAllChangesSeen, isPending: isMarkingAllSeen } = markAllSeen()

  const changes = computed(() => query.data.value?.pages.flatMap(page => page.data) ?? [])
  // The first page's meta describes the whole result (total, cap); later pages only move the cursor.
  const meta = computed(() => query.data.value?.pages[0]?.meta ?? null)

  const hasActiveFilters = computed(() => view.reason.length > 0 || view.entityType.length > 0 || view.unseen)

  // The newest change this list knew about when it was last fetched. If the watchlist has since
  // recorded a newer one (it refreshes on realtime notifications), the list is out of date.
  const knownLatestChangeAt = ref(null)
  // Distinguishes "no baseline fetched yet" from "the baseline legitimately has no prior
  // change" — a plain null check on knownLatestChangeAt can't tell those apart, since a
  // watchlist's first-ever change looks identical (null -> timestamp) to that initial seed.
  // Getting this wrong silently ate the very first realtime arrival on an empty watchlist.
  const hasSeededKnownLatest = ref(false)
  watch(query.dataUpdatedAt, () => {
    hasSeededKnownLatest.value = true
    knownLatestChangeAt.value = toValue(watchlist)?.latestChangeAt ?? null
  }, { immediate: true })
  // The watchlist resource can resolve before the changes-list query does; seed the baseline
  // from it early so hasNewChanges doesn't false-positive merely because the list hasn't
  // loaded yet. Only applies until the list's own first fetch establishes the real baseline.
  watch(() => toValue(watchlist)?.latestChangeAt, latest => {
    if (!hasSeededKnownLatest.value) knownLatestChangeAt.value = latest ?? null
  })
  const hasNewChanges = computed(() => {
    const latest = toValue(watchlist)?.latestChangeAt
    if (!latest) return false
    if (!knownLatestChangeAt.value) return true
    return new Date(latest) > new Date(knownLatestChangeAt.value)
  })

  const refresh = () => query.refetch()
  // Auto-pull a realtime arrival in immediately rather than making the user click the
  // Refresh chip for something a websocket push already told them about.
  watch(hasNewChanges, val => {
    if (val) refresh()
  })
  const fetchOlder = () => query.fetchNextPage()

  const markChange = changeId => {
    const change = changes.value.find(item => item.id === changeId)
    if (!change || change.seenAt) return Promise.resolve()
    return markChangeSeen({ watchlistId: toValue(watchlistId), changeId })
  }
  const markAll = () => markAllChangesSeen(toValue(watchlistId))

  return {
    view,
    setView,
    resetFilters,
    hasActiveFilters,
    changes,
    meta,
    isPending: query.isPending,
    isError: query.isError,
    isFetchingOlder: query.isFetchingNextPage,
    hasOlder: query.hasNextPage,
    fetchOlder,
    hasNewChanges,
    refresh,
    markChange,
    markAll,
    isMarkingAllSeen
  }
}
